import React, { useEffect, useState } from 'react';
import { Card, Footer, Information, SubHeader } from 'components';
import { Layout, Slider, Progress, Button, Modal } from 'components/sdk';
import { Col, Radio, Row, Table } from 'antd';
import { formatAndNotificateError, formatString } from 'utils';
import { useMutation } from '@apollo/client';
import { useBusinessLoanContext } from 'contexts/BusinessLoanContext';
import { SAVE_LOAN, SAVE_LOAN_EVENT } from 'screens/PreApprovedScreen/graphql';

import './styles.less';

const { Content } = Layout;
const BadesulMPEPreApproved = () => {
  const { me, currentContext, contextLoading, contextRefetch } = useBusinessLoanContext();
  const { loan } = currentContext;

  const [chosenAmount, setChosenAmount] = useState(loan.finalAmount);
  // eslint-disable-next-line no-unused-vars
  const [installmentAmount, setInstallmentAmount] = useState(0);
  const [goingToNextStep, setGoingToNextStep] = useState(false);
  const [preApprovedOption, setPreApprovedOption] = useState({});
  const [selectedTaxRateType, setSelectedTaxRateType] = useState();

  const [gambiInstallments, setGambiInstallments] = useState([]);
  const [modalInstallmentsVisible, setModalInstallmentsVisible] = useState(false);

  const [saveLoan, { loading: saveLoading }] = useMutation(SAVE_LOAN, {
    onError: (error) => formatAndNotificateError(error),
  });

  const [saveLoanEvent, { loading: saveLoanLoading }] = useMutation(SAVE_LOAN_EVENT, {
    onCompleted: () => {
      contextRefetch();
      setGoingToNextStep(true);
    },
    onError: (error) => formatAndNotificateError(error),
  });

  const handleConfirmModalSubmit = async () => {
    await saveLoan({
      variables: {
        id: currentContext?.loan.id,
        paymentDay: 10,
      },
    });

    await saveLoanEvent({
      variables: {
        loanId: currentContext?.loan.id,
        amount: chosenAmount,
        statusValue: 'pending',
        installmentsCount: preApprovedOption.finalInstallmentsCount,
        taxRateType: selectedTaxRateType,
      },
    });
  };

  useEffect(() => {
    if (currentContext) {
      const preApprovedOption = currentContext.loan.preApprovedOptions[0];
      setPreApprovedOption(preApprovedOption);
      setSelectedTaxRateType(
        currentContext.loan.additionalInfo?.taxRateType ||
          currentContext.loan.preApprovedOptions[0].taxRateType,
      );
    }
  }, [currentContext]);

  useEffect(() => {
    setChosenAmount(preApprovedOption.finalAmount);
    setInstallmentAmount(preApprovedOption.finalInstallmentsAmount);
  }, [preApprovedOption]);

  useEffect(() => {
    const { installmentAmount, finalAmount } = preApprovedOption;
    const rate = chosenAmount / finalAmount;

    setInstallmentAmount(installmentAmount * rate);
  }, [chosenAmount, preApprovedOption]);

  useEffect(() => {
    if (preApprovedOption?.additionalInfo?.installments) {
      let str = preApprovedOption.additionalInfo.installments;
      // remove first string of str
      str = str.substring(str.indexOf('['), str.lastIndexOf(']') + 1);
      str = str.replaceAll('\\', '');
      const parsed = JSON.parse(`{"installments": ${str}}`).installments;
      setGambiInstallments(parsed);
    }
  }, [preApprovedOption]);

  const loading = saveLoading || saveLoanLoading || contextLoading || goingToNextStep;

  return (
    <Layout id="pre-approved-screen">
      <SubHeader title={`Olá, ${me?.firstName}! Veja abaixo um resumo da sua solicitação.`} />

      <Content className="badesul-pronampe-pre-approved-content">
        <Card className="pre-approved-card">
          <Row
            gutter={[56, 26]}
            align="middle"
            justify="space-around"
            className="badesul-pronampe-pre-approved-card-content"
          >
            <Col xs={24} sm={24} md={14}>
              <Row>
                <Col span={24} className="pre-approved-progress-container">
                  <Progress
                    className="pre-approved-progress"
                    type="circle"
                    percent={100}
                    strokeColor="var(--primary-button-color)"
                    strokeWidth={9}
                  />

                  <div className="pre-approved-progress-content">
                    <p className="no-margin-bottom pre-approved-progress-label secondary-text">
                      Valor
                      <br />
                      disponível
                    </p>

                    <h5>
                      <strong>
                        {formatString(preApprovedOption.finalAmount, 'currency')?.split('.')[0]} mil
                      </strong>
                    </h5>
                  </div>
                </Col>

                <Col span={24}>
                  <h5>
                    <b>Valor solicitado</b>
                  </h5>

                  <Slider
                    tooltipVisible={false}
                    min={20000}
                    max={preApprovedOption.finalAmount}
                    value={chosenAmount}
                    onChange={(e) => setChosenAmount(e)}
                    step={500}
                    showValues
                    valuesFormatter={(e) => formatString(e, 'currency')}
                    input={{ type: 'currency' }}
                    loading={loading}
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={24} sm={24} md={10}>
              <Row align="middle" gutter={[16, 20]}>
                <Col span={24}>
                  <Information
                    loading={loading}
                    label="Total pré-aprovado"
                    value={formatString(preApprovedOption.finalAmount, 'currency')}
                    type=""
                  />
                </Col>
                <Col span={24}>
                  <Information
                    loading={loading}
                    label="Taxa"
                    value={
                      <Radio.Group
                        onChange={(e) => setPreApprovedOption(e.target.value)}
                        value={preApprovedOption}
                      >
                        {loan.preApprovedOptions.map((option) => (
                          <Radio value={option}>
                            <span style={{ fontSize: 24 }}>{option.taxRateType}</span>
                          </Radio>
                        ))}
                      </Radio.Group>
                    }
                  />
                </Col>
                <Col span={24}>
                  <Information
                    loading={loading}
                    label="Carência"
                    // eslint-disable-next-line max-len
                    value="6 meses"
                  />
                </Col>

                <Col span={24}>
                  <Information
                    loading={loading}
                    label="Parcelas"
                    // eslint-disable-next-line max-len
                    value={
                      <div className="badesul-mpe-pre-approved-installments-text">
                        2 trimestrais de carência e 54 mensais de amortização {'\n'}
                        <Button
                          className="badesul-mpe-pre-approved-button-to-show"
                          color="link"
                          onClick={() => {
                            setModalInstallmentsVisible(true);
                          }}
                        >
                          Veja detalhado
                        </Button>
                      </div>
                    }
                  />
                </Col>

                <Col span={24}>
                  <Information loading={loading} label="Vencimento" value="Todo dia 15" />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        <div className="badesul-mpe-pre-approved-disclaimer">
          Financiamento possui taxa de análise (0,8% do valor contratado) que deve ser paga antes da
          assinatura da CCB (Cédula de Crédito Bancário)
          <br />
          Financiamento com incidência de IOF e desconto da taxa de contratação do fundo garantidor
          FGI PEAC
        </div>
      </Content>
      <Modal
        title="Parcelas detalhadas"
        visible={modalInstallmentsVisible}
        onCancel={() => setModalInstallmentsVisible(false)}
        footer={null}
        width={800}
        className="badesul-mpe-pre-approved-installments-modal"
      >
        As parcelas serão ajustadas proporcionalmente ao valor solicitado, custo do fundo garantidor
        e a possíveis variações do indexador.
        <Table
          dataSource={gambiInstallments}
          pagination={false}
          columns={[
            {
              title: 'Parcela',
              dataIndex: 'mes',
              key: 'month',
            },
            {
              title: 'Principal',
              dataIndex: 'principal',
              key: 'principal',
              render: (value) => formatString(value, 'currency'),
            },
            {
              title: 'Juros',
              dataIndex: 'juros',
              key: 'interest',
              render: (value) => formatString(value, 'currency'),
            },
            {
              title: 'Valor da parcela',
              dataIndex: 'valorParcela',
              key: 'installmentValue',
              render: (value) => formatString(value, 'currency'),
            },
          ]}
        />
      </Modal>

      <Footer
        loading={loading}
        okButton={{
          text: 'Finalizar ajustes',
          loading,
          onClick: () => {
            handleConfirmModalSubmit();
          },
        }}
      />
    </Layout>
  );
};

export default BadesulMPEPreApproved;
